/**
 * Checkout Stripe nativo dal portale Suite.
 *
 * Crea una Checkout Session per l'utente e l'attivita' (POST /api/checkout/:service/session):
 * la session porta product_doid, activity_id e user_id, e il webhook Suite attiva il piano da
 * solo al pagamento. Dal 2026-10-08 sostituisce i link statici (prima carrelli GHL, poi la
 * pagina doid.it/suite/abbonamento/ con attivazione a mano).
 *
 * Se la sessione non si puo' creare (servizio senza checkout nativo, price non configurato,
 * errore di rete) ripiega sul link statico del servizio, poi su WhatsApp: il cliente non resta
 * mai senza una strada per pagare.
 */

import api from './api.js';

// Servizi con checkout nativo nel backend (routes/checkout.js). Hub non c'e': il webhook non
// sa ancora attivare il bundle.
const NATIVE_CHECKOUT_SERVICES = ['review', 'page', 'menu'];

const WHATSAPP_NUMBER = '393516781324';

/**
 * @param {object} opts
 * @param {string} opts.serviceCode - review | page | menu | ...
 * @param {string} opts.activityId - attivita' a cui legare l'abbonamento
 * @param {'monthly'|'yearly'} [opts.billingCycle='monthly']
 * @param {string} [opts.fallbackUrl] - link statico del servizio (services.payment_url)
 * @param {string} [opts.serviceName] - per il messaggio WhatsApp di ripiego
 * @param {string} [opts.activityName] - per il messaggio WhatsApp di ripiego
 * @returns {Promise<void>} non torna se reindirizza a Stripe
 */
export async function startCheckout({
  serviceCode,
  activityId,
  billingCycle = 'monthly',
  fallbackUrl,
  serviceName,
  activityName = '',
}) {
  const plan = billingCycle === 'yearly' ? 'yearly' : 'monthly';

  if (NATIVE_CHECKOUT_SERVICES.includes(serviceCode) && activityId) {
    try {
      const res = await api.request(`/checkout/${serviceCode}/session`, {
        method: 'POST',
        body: JSON.stringify({ plan, activityId, returnTo: 'suite' }),
      });
      if (res?.success && res.data?.url) {
        window.location.assign(res.data.url);
        return;
      }
      console.warn('[checkout] sessione Stripe non creata, uso il ripiego:', res?.error);
    } catch (error) {
      console.warn('[checkout] errore nella creazione della sessione, uso il ripiego:', error);
    }
  }

  if (fallbackUrl) {
    window.location.assign(fallbackUrl);
    return;
  }

  const ciclo = plan === 'yearly' ? 'annuale' : 'mensile';
  const msg = encodeURIComponent(
    `Ciao, vorrei rinnovare/attivare il servizio *${serviceName || serviceCode}* per l'attività *${activityName}* (ciclo: ${ciclo}). Grazie!`
  );
  window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank');
}

/**
 * Legge l'esito del ritorno da Stripe (?checkout=success|cancel&service=...) e pulisce l'URL.
 * @returns {{ status: 'success'|'cancel', service: string } | null}
 */
export function readCheckoutReturn() {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('checkout');
  if (status !== 'success' && status !== 'cancel') return null;
  const service = params.get('service') || '';
  params.delete('checkout');
  params.delete('service');
  const rest = params.toString();
  window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
  return { status, service };
}
